import { extends as _extends, objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties } from '../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useCheckboxGroupContext } from './checkbox-group.esm.js';
import CheckboxIcon from './checkbox-icon.esm.js';
import { useCheckbox } from './use-checkbox.esm.js';
import { resolveInputOutline } from '../../styles-system/input-element-utils.esm.js';
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui.esm.js';
import extractStringNodes from '../../utils/extract-string-nodes.esm.js';
import { callAll } from '../../utils/function.esm.js';
import { get, EMPTY_OBJECT } from '../../utils/object.esm.js';
import { resolveSize } from '../../utils/size-utils.esm.js';
import { resolveColor } from '../../utils/styled-props.esm.js';
import { BoxPrivate } from '../_primitives/box/box.esm.js';
import { FlexPrivate } from '../_primitives/flex/flex.esm.js';
import FlexInline from '../_primitives/flex/flex-inline.esm.js';
import Icon from '../_primitives/icon/icon.esm.js';
import { InputBase } from '../_primitives/input/input.esm.js';
import Text from '../_primitives/text/text.esm.js';
import { formikField } from '../_adaptors/fields.esm.js';

var _excluded = ["className", "readOnly", "disabled", "checked", "indeterminate", "label", "labelIndicator", "labelLocation", "children", "state", "size", "variant", "checkIcon", "iconSize", "iconColor", "onChange", "iconBg", "bg", "background", "backgroundColor", "isInvalid", "disableColor", "disableBg", "spacing", "labelProps", "active", "hover", "width", "minWidth"];
var CheckboxInputBase = /*#__PURE__*/React.forwardRef(function _CheckboxInputBase(props, ref) {
  // @ts-ignore
  return /*#__PURE__*/React.createElement(InputBase, _extends({
    ref: ref
  }, props));
});
var textWrapperSx = {
  overflow: 'hidden'
};
var CheckboxPrivate = /*#__PURE__*/React.forwardRef(function _CheckboxPrivate(props, ref) {
  var group = useCheckboxGroupContext();
  var mergedProps = _objectSpread2(_objectSpread2({}, group), props);
  var className = mergedProps.className,
    readOnly = mergedProps.readOnly,
    _mergedProps$disabled = mergedProps.disabled,
    disabled = _mergedProps$disabled === void 0 ? readOnly || (group === null || group === void 0 ? void 0 : group.disabled) : _mergedProps$disabled,
    isCheckedProp = mergedProps.checked,
    isIndeterminate = mergedProps.indeterminate,
    labelProp = mergedProps.label,
    labelIndicator = mergedProps.labelIndicator,
    _mergedProps$labelLoc = mergedProps.labelLocation,
    labelLocation = _mergedProps$labelLoc === void 0 ? 'right' : _mergedProps$labelLoc,
    children = mergedProps.children,
    stateProps = mergedProps.state,
    size = mergedProps.size,
    variant = mergedProps.variant,
    _mergedProps$checkIco = mergedProps.checkIcon,
    CheckIcon = _mergedProps$checkIco === void 0 ? /*#__PURE__*/React.createElement(CheckboxIcon, null) : _mergedProps$checkIco,
    iconSizeProps = mergedProps.iconSize,
    iconColor = mergedProps.iconColor,
    onChangeProp = mergedProps.onChange,
    iconBgProps = mergedProps.iconBg,
    bg = mergedProps.bg,
    background = mergedProps.background,
    backgroundColor = mergedProps.backgroundColor,
    isInvalid = mergedProps.isInvalid,
    disableColor = mergedProps.disableColor,
    disableBg = mergedProps.disableBg,
    _mergedProps$spacing = mergedProps.spacing,
    gap = _mergedProps$spacing === void 0 ? 'sm' : _mergedProps$spacing,
    _mergedProps$labelPro = mergedProps.labelProps,
    _labelProps = _mergedProps$labelPro === void 0 ? EMPTY_OBJECT : _mergedProps$labelPro,
    active = mergedProps.active,
    hover = mergedProps.hover,
    _mergedProps$width = mergedProps.width,
    width = _mergedProps$width === void 0 ? 'auto' : _mergedProps$width,
    _mergedProps$minWidth = mergedProps.minWidth,
    minWidth = _mergedProps$minWidth === void 0 ? '0' : _mergedProps$minWidth,
    rest = _objectWithoutProperties(mergedProps, _excluded);
  var theme = useSafeThemeUI(props);
  var fontSize = size ? resolveSize(size, get(theme, 'sizes.font')) : undefined;
  var state = isInvalid ? 'danger' : stateProps;
  var iconSize = iconSizeProps ? resolveSize(iconSizeProps, get(theme, 'sizes.icon')) : '1em';
  var iconBg = iconBgProps || bg || backgroundColor || background;
  var isChecked = isCheckedProp;
  if (group !== null && group !== void 0 && group.value && mergedProps.value) {
    isChecked = group.value.includes(mergedProps.value);
  }
  var stringNodes = extractStringNodes(React.Children.toArray(children).filter(function (i) {
    return typeof i === 'string';
  }));
  var renderNodes = React.Children.toArray(children).filter(function (i) {
    return typeof i !== 'string' && typeof i !== 'undefined' && typeof i !== 'number' && typeof i !== 'boolean';
  });
  var label = labelProp !== null && labelProp !== void 0 ? labelProp : stringNodes.length > 0 ? "".concat(stringNodes) : undefined;
  var onChange = onChangeProp;
  if (group !== null && group !== void 0 && group.onChange && mergedProps.value) {
    onChange = callAll(group.onChange, onChangeProp);
  }
  var _useCheckbox = useCheckbox(_objectSpread2(_objectSpread2({
      forwardedRef: ref,
      'aria-label': label
    }, rest), {}, {
      isDisabled: disabled,
      isChecked: isChecked,
      isInvalid: isInvalid,
      isIndeterminate: isIndeterminate,
      onChange: onChange,
      labelProps: _labelProps
    })),
    inputState = _useCheckbox.state,
    inputProps = _useCheckbox.inputProps,
    checkboxProps = _useCheckbox.checkboxProps,
    labelProps = _useCheckbox.labelProps,
    rootProps = _useCheckbox.rootProps;
  var controllerStyle = useMemo(function () {
    return _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, props.checkIcon ? {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    } : {}), {}, {
      appearance: 'none',
      bg: iconBg ? iconBg : 'white'
    }, iconColor ? {
      color: iconColor
    } : {}), {}, {
      borderRadius: '0.2em'
    }, resolveInputOutline(theme, state ? state : 'border')), {}, {
      height: iconSize,
      position: 'relative',
      minWidth: iconSize,
      width: iconSize
    });
  }, [iconBg, iconColor, iconSize, state, theme, props.checkIcon]);
  var iconStyles = useMemo(function () {
    return {
      opacity: inputState.isChecked || inputState.isIndeterminate ? 1 : 0,
      transform: inputState.isChecked || inputState.isIndeterminate ? 'scale(1)' : 'scale(0.95)',
      transition: 'transform 200ms'
    };
  }, [inputState.isChecked, inputState.isIndeterminate]);
  var clonedIcon = /*#__PURE__*/React.cloneElement(CheckIcon, {
    __css: iconStyles,
    isIndeterminate: variant !== 'switch' && inputState.isIndeterminate,
    isChecked: variant !== 'switch' && inputState.isChecked
  });
  var innerContent = useMemo(function () {
    return renderNodes.length > 0 ? renderNodes : label ? /*#__PURE__*/React.createElement(Text, _extends({
      className: "csg-tui-checkbox-label",
      ellipsis: true
    }, labelProps), label) : null;
  }, [labelProps, label, renderNodes]);
  var content = useMemo(function () {
    return renderNodes.length > 0 ? renderNodes : label && labelIndicator ? /*#__PURE__*/React.createElement(FlexInline, {
      as: "span",
      gap: "xs-3",
      className: "csg-tui-label-text-wrapper",
      sx: textWrapperSx
    }, innerContent, labelIndicator) : label ? innerContent : null;
  }, [innerContent, label, labelIndicator, renderNodes]);
  var wrapperBaseStyles = useMemo(function () {
    return {
      alignItems: 'center',
      cursor: readOnly ? 'auto' : disabled ? 'not-allowed' : 'pointer'
    };
  }, [disabled, readOnly]);
  var iconBaseStyles = useMemo(function () {
    return {
      height: iconSize,
      width: iconSize
    };
  }, [iconSize]);
  return /*#__PURE__*/React.createElement(FlexPrivate, _extends({
    as: renderNodes.length > 0 ? undefined : 'label',
    className: "csg-tui-checkbox",
    fontSize: fontSize,
    gap: gap,
    width: width,
    minWidth: minWidth
  }, rootProps, {
    __css: wrapperBaseStyles
  }), labelLocation === 'left' && content, /*#__PURE__*/React.createElement(CheckboxInputBase, _extends({
    className: classNames('csg-tui-checkbox-input', className, {
      'is-active': active,
      'is-hover': hover,
      'is-read-only': readOnly
    }),
    variant: variant ? "checkbox.".concat(variant) : 'checkbox.default',
    state: state,
    iconBg: iconBg,
    iconColor: iconColor,
    disableBg: disableBg,
    disableColor: disableColor
  }, inputProps)), variant === 'checkIcon' ? /*#__PURE__*/React.createElement(Icon, {
    icon: inputState.isIndeterminate ? 'remove' : inputState.isChecked ? 'selected' : 'select',
    size: iconSize,
    secondaryColor: inputState.isDisabled ? resolveColor(theme, 'neutrals.17') : undefined,
    __css: iconBaseStyles
  }) : variant === 'classicSuite' ? /*#__PURE__*/React.createElement(FlexPrivate, _extends({
    className: "csg-tui-checkbox-control"
  }, checkboxProps, {
    __css: controllerStyle,
    justifyContent: "center"
  })) : /*#__PURE__*/React.createElement(BoxPrivate, _extends({
    className: "csg-tui-checkbox-control"
  }, checkboxProps, {
    __css: controllerStyle
  }), clonedIcon), labelLocation === 'right' && content);
});
var MemoCheckbox = /*#__PURE__*/React.memo(CheckboxPrivate);

//@ts-ignore
var Checkbox = /*#__PURE__*/React.forwardRef(function Checkbox(props, ref) {
  return /*#__PURE__*/React.createElement(MemoCheckbox, _extends({
    ref: ref
  }, props));
});
Checkbox.Formik = formikField(Checkbox, {
  isCheckbox: true
});

export default Checkbox;
//# sourceMappingURL=checkbox.esm.js.map
